import { walkElevationPolyline, type WalkSupportSurface } from './traversal-3d.js';
type Point = { x: number; y: number; z: number };
type Row = Record<string, unknown>;
const row = (v: unknown): Row => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : {};

/** Existing step-height rules apply to adjacent rectangular room floors too.
 * Derive these support faces from held shape truth; inventory chains remain separate. */
export function rectangularRoomStepSurfaces(boxes: readonly Row[], origin: Point, destination: Point, thresholdFt: number,
    route: ReadonlyArray<readonly number[]> = [[origin.x,origin.y],[destination.x,destination.y]],
    architecture?: { openings: readonly unknown[]; radiusFt: number; heightFt: number }): WalkSupportSurface[] {
    if (!Number.isFinite(thresholdFt) || thresholdFt <= 0 || route.length < 2) return [];
    const radius = architecture?.radiusFt ?? 0;
    if (!Number.isFinite(radius) || radius < 0) return [];
    const interval=(p:readonly number[],q:readonly number[],f:WalkSupportSurface['footprint']):[number,number]|null=>{
        let lo=0,hi=1;
        for(const [axis,min,max] of [[0,f.minX,f.maxX],[1,f.minY,f.maxY]] as const){
            const at=p[axis],to=q[axis];if(!Number.isFinite(at)||!Number.isFinite(to))return null;
            const d=to!-at!;
            if(Math.abs(d)<1e-12){if(at!<min-1e-6||at!>max+1e-6)return null;}
            else{const t1=(min-at!)/d,t2=(max-at!)/d;lo=Math.max(lo,Math.min(t1,t2));hi=Math.min(hi,Math.max(t1,t2));if(hi<lo-1e-6)return null;}
        }
        return [lo,hi];
    };
    const faces = boxes.map(box => {
        const c = row(box.creation), fp = row(c.footprint), x = row(box.x), y = row(box.y);
        const minX = Number(x.min), maxX = Number(x.max), minY = Number(y.min), maxY = Number(y.max), z = Number(box.floor);
        const closed = box.interior === 'closed' && c.enclosure === 'walls-and-ceiling';
        const open = box.interior === 'open' && c.enclosure === 'none';
        return { id: 'room-floor:' + String(box.id), z, footprint: {minX,maxX,minY,maxY}, closed,
            modelled: (open || closed) && c.renderSurfaces !== false && (fp.kind === undefined || fp.kind === 'rect')
                && (!closed || !!architecture) && (!architecture || Number(box.ceiling) >= z + architecture.heightFt - 1e-6) };
    }).filter(f => [f.z,...Object.values(f.footprint)].every(Number.isFinite)
        && f.footprint.maxX > f.footprint.minX && f.footprint.maxY > f.footprint.minY);
    const contains = (f: WalkSupportSurface, p: Point) => p.x >= f.footprint.minX - 1e-6 && p.x <= f.footprint.maxX + 1e-6
        && p.y >= f.footprint.minY - 1e-6 && p.y <= f.footprint.maxY + 1e-6;
    const floorAt = (p: Point) => faces.filter(f => f.modelled && Math.abs(f.z-p.z) <= 1e-6 && contains(f,p))
        .sort((a,b) => ((a.footprint.maxX-a.footprint.minX)*(a.footprint.maxY-a.footprint.minY))
            - ((b.footprint.maxX-b.footprint.minX)*(b.footprint.maxY-b.footprint.minY)) || a.id.localeCompare(b.id))[0];
    const a = floorAt(origin), b = floorAt(destination);
    if (!a || !b) return [];
    const selected = new Set([a,b]);
    // Split at EVERY crossed face boundary. At each interval the highest
    // physical floor wins; underlying aprons/foundations are not extra steps.
    // Floors wholly above the body's route envelope are overhead architecture.
    const candidates = faces.filter(f => f.z < Math.max(origin.z,destination.z) + (architecture?.heightFt ?? thresholdFt) - 1e-6);
    for (let i=1; i<route.length; i++) {
        const from=route[i-1]!, to=route[i]!;
        const spans=candidates.map(f => ({f,span:interval(from,to,f.footprint)})).filter(v => v.span !== null);
        const cuts=[...new Set([0,1,...spans.flatMap(v => v.span!)])].sort((a,b)=>a-b);
        for (let j=1; j<cuts.length; j++) {
            if (cuts[j]!-cuts[j-1]! <= 1e-7) continue;
            const t=(cuts[j]!+cuts[j-1]!)/2;
            const top=spans.filter(v => v.span![0] <= t && v.span![1] >= t).sort((a,b)=>b.f.z-a.f.z)[0]?.f;
            if (!top || !top.modelled || (top.closed && top !== a && top !== b)) return [];
            selected.add(top);
        }
    }
    const floors=[...selected].sort((a,b)=>a.z-b.z);
    const profile=walkElevationPolyline(route,origin.z,destination.z,floors,Math.min(origin.z,destination.z),thresholdFt);
    for (const link of profile.links) {
        if (Math.abs(link.toZ-link.fromZ) > thresholdFt + 1e-6) return [];
        const at={x:link.x,y:link.y,z:0};
        const from=floors.filter(f=>Math.abs(f.z-link.fromZ)<=1e-6&&contains(f,at));
        const to=floors.filter(f=>Math.abs(f.z-link.toZ)<=1e-6&&contains(f,at));
        if (!from.some(a=>to.some(b=>{
            const x=Math.min(a.footprint.maxX,b.footprint.maxX)-Math.max(a.footprint.minX,b.footprint.minX);
            const y=Math.min(a.footprint.maxY,b.footprint.maxY)-Math.max(a.footprint.minY,b.footprint.minY);
            return x>=-1e-6&&y>=-1e-6&&(x>1e-6||y>1e-6)
                && !(x<=1e-6&&y<radius*2-1e-6) && !(y<=1e-6&&x<radius*2-1e-6);
        }))) return [];
    }
    for(let i=1;i<route.length;i++){
        const spans=floors.map(f=>interval(route[i-1]!,route[i]!,f.footprint))
            .filter((v):v is [number,number]=>v!==null).sort((x,y)=>x[0]-y[0]);
        let through=0;
        for(const span of spans){if(span[0]>through+1e-6)return [];through=Math.max(through,span[1]);}
        if(through<1-1e-6)return [];
    }
    if (radius > 0) {
        // Partition the bounding rectangle at every floor edge. Each cell is
        // wholly supported or wholly empty; keep the swept circular body at
        // least its radius from every empty cell, including concave corners.
        const footprints = floors.map(f=>f.footprint);
        const xs = [...new Set(footprints.flatMap(f => [f.minX, f.maxX]))].sort((a,b) => a-b);
        const ys = [...new Set(footprints.flatMap(f => [f.minY, f.maxY]))].sort((a,b) => a-b);
        if (route.some(p => p[0]! - radius < xs[0]! - 1e-6 || p[0]! + radius > xs[xs.length-1]! + 1e-6
            || p[1]! - radius < ys[0]! - 1e-6 || p[1]! + radius > ys[ys.length-1]! + 1e-6)) return [];
        const pointSegmentDistance = (p: readonly number[], from: readonly number[], to: readonly number[]): number => {
            const dx = to[0]! - from[0]!, dy = to[1]! - from[1]!;
            const length2 = dx*dx + dy*dy;
            const t = length2 > 0 ? Math.max(0, Math.min(1, ((p[0]!-from[0]!)*dx + (p[1]!-from[1]!)*dy)/length2)) : 0;
            return Math.hypot(p[0]! - from[0]! - t*dx, p[1]! - from[1]! - t*dy);
        };
        for (let xi = 1; xi < xs.length; xi++) for (let yi = 1; yi < ys.length; yi++) {
            const cell = { minX: xs[xi-1]!, maxX: xs[xi]!, minY: ys[yi-1]!, maxY: ys[yi]! };
            const cx = (cell.minX+cell.maxX)/2, cy = (cell.minY+cell.maxY)/2;
            if (footprints.some(f => cx >= f.minX && cx <= f.maxX && cy >= f.minY && cy <= f.maxY)) continue;
            const corners = [[cell.minX,cell.minY],[cell.minX,cell.maxY],[cell.maxX,cell.minY],[cell.maxX,cell.maxY]];
            for (let i = 1; i < route.length; i++) {
                const from = route[i-1]!, to = route[i]!;
                const distance = interval(from,to,cell) ? 0 : Math.min(
                    ...corners.map(p => pointSegmentDistance(p,from,to)),
                    ...[from,to].map(p => Math.hypot(Math.max(cell.minX-p[0]!,0,p[0]!-cell.maxX),
                        Math.max(cell.minY-p[1]!,0,p[1]!-cell.maxY))),
                );
                if (distance < radius - 1e-6) return [];
            }
        }
    }
    // Closed room steps still owe the actual doorway: every crossed boundary
    // must be a passable aperture wide/tall enough for the mover.
    for(const room of floors.filter(s=>s.closed)){
        const f=room.footprint,ctx=architecture!;
        const strict=(p:readonly number[])=>p[0]!>f.minX+1e-6&&p[0]!<f.maxX-1e-6&&p[1]!>f.minY+1e-6&&p[1]!<f.maxY-1e-6;
        for(let i=1;i<route.length;i++){
            const from=route[i-1]!,to=route[i]!,span=interval(from,to,f);if(!span||span[1]-span[0]<1e-7)continue;
            const crossings=[...(!strict(from)?[span[0]]:[]),...(!strict(to)?[span[1]]:[])];
            for(const t of crossings){
                const x=from[0]!+(to[0]!-from[0]!)*t,y=from[1]!+(to[1]!-from[1]!)*t;
                const through=ctx.openings.some(value=>{
                    const o=row(value);
                    const p=row(o.plane),s=row(o.span),z=row(o.z),axis=p.axis,at=Number(p.at),along=axis==='x'?y:x;
                    const boundary=axis==='x'?(Math.abs(at-f.minX)<1e-6||Math.abs(at-f.maxX)<1e-6):(axis==='y'&&(Math.abs(at-f.minY)<1e-6||Math.abs(at-f.maxY)<1e-6));
                    return o.passable===true&&boundary&&Math.abs((axis==='x'?x:y)-at)<1e-6&&along-ctx.radiusFt>=Number(s.min)-1e-6&&along+ctx.radiusFt<=Number(s.max)+1e-6&&Number(z.min)<=room.z+1e-6&&Number(z.max)>=room.z+ctx.heightFt-1e-6;
                });
                if(!through)return [];
            }
        }
    }
    // When a lower apron overlaps a raised floor, the higher physical surface
    // wins until its boundary, independently of travel direction.
    return floors;
}
